/**
 * KpiCard — card "indicatore" per Dashboard D01 e Statistiche D09.
 *
 * Anatomia:
 * - label (uppercase tracking-wide, font-label-sm)
 * - hint (sottotitolo opzionale sotto la label, testo secondario)
 * - valore grande tabular-nums (font-data-tabular, scala 3xl-4xl)
 * - sub (badge informativo opzionale: "Dati non ancora disponibili",
 *   "+12% vs mese scorso", ecc.)
 *
 * Quando i dati sono in caricamento, passare `loading={true}` per mostrare
 * un skeleton animato al posto del valore e del badge.
 * Quando i dati non sono disponibili il chiamante passa `value="—"` e
 * `subTone="neutral"` con un messaggio chiaro.
 */

import { Card } from '@shared/components/ui/Card';
import { Badge, type BadgeTone } from '@shared/components/ui/Badge';
import { Icon } from '@shared/components/ui/Icon';

export interface KpiCardProps {
  label: string;
  /** Valore (string per gestire formati € 1.234,56 o "—"). */
  value: string;
  /** Mini spiegazione sotto la label (non uppercase, non badge). */
  hint?: string;
  /** Sub-label sotto il valore (es. "+12% vs scorso mese"). */
  sub?: string;
  /** Tone della pill sub. */
  subTone?: BadgeTone;
  /** Icona opzionale top-right (Material Symbols name). */
  icon?: string;
  className?: string;
  /**
   * Quando true mostra skeleton pulse invece del valore e del badge.
   * Label e icona restano visibili per evitare layout shift.
   */
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  hint,
  sub,
  subTone = 'neutral',
  icon,
  className,
  loading = false,
}: KpiCardProps) {
  return (
    <Card className={className}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-label-sm text-label-sm uppercase tracking-wide text-text-secondary">
            {label}
          </p>
          {hint && (
            <p className="mt-1 font-body-sm text-body-sm text-text-secondary normal-case tracking-normal">
              {hint}
            </p>
          )}
        </div>
        {icon && (
          <Icon
            name={icon}
            size={20}
            className="text-text-secondary/70 flex-shrink-0"
          />
        )}
      </div>
      {loading ? (
        <div
          className="mt-2 h-9 w-16 rounded bg-bg-card animate-pulse"
          aria-label="Caricamento"
          role="status"
        />
      ) : (
        <p className="mt-2 font-data-tabular text-3xl md:text-4xl font-bold text-text-main tabular-nums leading-none">
          {value}
        </p>
      )}
      {loading ? (
        sub ? (
          <div className="mt-3 h-5 w-28 rounded bg-bg-card animate-pulse" aria-hidden="true" />
        ) : null
      ) : (
        sub && (
          <div className="mt-3">
            <Badge tone={subTone}>{sub}</Badge>
          </div>
        )
      )}
    </Card>
  );
}
